"use client";

import { useEffect, useMemo, useState } from "react";
import axios from "@/lib/api";

/** API response shapes from controllers/dashboardAnalyticsController.js */
type RegionRow = { label: string; total: number; count: number };
type DistrictRow = {
  label: string; // "Southern - Mangochi"
  region: "Southern" | "Eastern" | "Central" | "Northern";
  district: string;
  total: number;
  count: number;
};

type Mode = "region" | "district";

function firstDayThisMonthLocal(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function todayLocal(): string {
  return new Date().toISOString().slice(0, 10);
}
const fmtMK = (n: number) => `MK${n.toLocaleString("en-MW")}`;

export default function RegionRevenueDrilldown() {
  const [mode, setMode] = useState<Mode>("region");
  const [selectedRegion, setSelectedRegion] = useState<
    "Southern" | "Eastern" | "Central" | "Northern" | null
  >(null);

  const [start, setStart] = useState<string>(firstDayThisMonthLocal());
  const [end, setEnd] = useState<string>(todayLocal());

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<(RegionRow | DistrictRow)[]>([]);
  const [error, setError] = useState<string | null>(null);

  const max = useMemo(
    () => Math.max(1, ...rows.map((r: any) => Number(r.total) || 0)),
    [rows]
  );
  const totalSum = useMemo(
    () => rows.reduce((a: number, r: any) => a + (Number(r.total) || 0), 0),
    [rows]
  );

  async function fetchRegion() {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get("/dashboard/revenue-breakdown", {
        params: { groupBy: "region", start, end },
      });
      setRows(data?.status === 1 ? (data.data as RegionRow[]) : []);
      setMode("region");
      setSelectedRegion(null);
    } catch (e: any) {
      setError(e?.message || "Failed to load region revenue");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDistrict(
    region: "Southern" | "Eastern" | "Central" | "Northern"
  ) {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get("/dashboard/revenue-breakdown", {
        params: { groupBy: "district", region, start, end },
      });
      setRows(data?.status === 1 ? (data.data as DistrictRow[]) : []);
      setMode("district");
      setSelectedRegion(region);
    } catch (e: any) {
      setError(e?.message || "Failed to load district revenue");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  // initial load
  useEffect(() => {
    fetchRegion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refetch on date change
  useEffect(() => {
    if (mode === "region") fetchRegion();
    else if (mode === "district" && selectedRegion)
      fetchDistrict(selectedRegion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  return (
    <div className="p-4 bg-white border shadow rounded-xl">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-blue-900">
            Revenue{" "}
            {mode === "region"
              ? "by Region"
              : `by District (${selectedRegion})`}
          </h2>
          <p className="text-xs text-gray-500">
            Click a region to drill down into districts. Click “Back to Regions”
            to reset.
          </p>
        </div>

        {/* Date filters */}
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block mb-1 text-xs text-gray-600">Start</label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="px-3 py-2 text-sm border rounded"
            />
          </div>
          <div>
            <label className="block mb-1 text-xs text-gray-600">End</label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="px-3 py-2 text-sm border rounded"
            />
          </div>

          {mode === "district" && (
            <button
              onClick={fetchRegion}
              className="px-3 py-2 ml-2 text-sm border rounded hover:bg-gray-50"
              aria-label="Back to Regions"
            >
              ← Back to Regions
            </button>
          )}
        </div>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="py-8 text-sm text-gray-600">Loading revenue…</div>
      )}
      {error && !loading && (
        <div className="py-3 text-sm text-red-600">Error: {error}</div>
      )}

      {/* Bars */}
      {!loading && !error && rows.length > 0 && (
        <>
          <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
            <div>Total</div>
            <div className="font-medium">{fmtMK(totalSum)}</div>
          </div>

          <div className="grid gap-3 mt-2">
            {rows.map((r: any) => {
              const value = Number(r.total) || 0;
              const pct = Math.max(3, Math.round((value / max) * 100)); // ensure visible bar
              const label =
                mode === "region"
                  ? (r.label as string)
                  : `${r.district} (${r.region as string})`;

              return (
                <button
                  key={r.label}
                  onClick={() => {
                    if (mode === "region") {
                      fetchDistrict(r.label);
                    }
                  }}
                  className={`text-left group rounded-lg border px-3 py-2 hover:bg-gray-50 transition ${
                    mode === "region" ? "cursor-pointer" : "cursor-default"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-800">
                      {label}
                    </div>
                    <div className="text-sm text-gray-600">
                      {fmtMK(value)}{" "}
                      <span className="text-xs text-gray-400">
                        ({r.count} bookings)
                      </span>
                    </div>
                  </div>
                  <div className="h-3 mt-2 bg-gray-100 rounded">
                    <div
                      className={`h-3 rounded ${
                        mode === "region" ? "bg-blue-600" : "bg-emerald-600"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="py-10 text-sm text-gray-500">
          No data for the selected window.
        </div>
      )}
    </div>
  );
}
